import React, { createContext, useContext, useEffect, useState } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('ifd_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [restaurant, setRestaurant] = useState(() => {
    try {
      const saved = localStorage.getItem('ifd_cart_restaurant');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('ifd_cart', JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    localStorage.setItem('ifd_cart_restaurant', JSON.stringify(restaurant));
  }, [restaurant]);

  const addItem = (dish, rest) => {
    // If adding from a different restaurant, reset the cart
    if (restaurant && rest && restaurant.id !== rest.id && items.length > 0) {
      const confirmReset = window.confirm(
        `Il tuo carrello contiene piatti di "${restaurant.name}". Vuoi svuotarlo e ordinare da "${rest.name}"?`
      );
      if (!confirmReset) return;
      setItems([{ ...dish, qty: 1 }]);
      setRestaurant(rest);
      setIsOpen(true);
      return;
    }
    if (rest) setRestaurant(rest);
    setItems((prev) => {
      const existing = prev.find((i) => i.id === dish.id);
      if (existing) {
        return prev.map((i) => (i.id === dish.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, { ...dish, qty: 1 }];
    });
    setIsOpen(true);
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      if (next.length === 0) setRestaurant(null);
      return next;
    });
  };

  const decrement = (id) => {
    setItems((prev) => {
      const next = prev
        .map((i) => (i.id === id ? { ...i, qty: i.qty - 1 } : i))
        .filter((i) => i.qty > 0);
      if (next.length === 0) setRestaurant(null);
      return next;
    });
  };

  const increment = (id) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty: i.qty + 1 } : i)));
  };

  const clearCart = () => {
    setItems([]);
    setRestaurant(null);
  };

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const count = items.reduce((sum, i) => sum + i.qty, 0);
  const deliveryFee = restaurant ? restaurant.deliveryFee : 0;
  const total = subtotal + (subtotal > 0 ? deliveryFee : 0);

  return (
    <CartContext.Provider
      value={{
        items,
        restaurant,
        isOpen,
        setIsOpen,
        addItem,
        removeItem,
        increment,
        decrement,
        clearCart,
        subtotal,
        deliveryFee,
        total,
        count,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
